import Journey from './Journey';
import LogDataType from 'telemetry/lib/schema/LogDataType';
import React from 'react';
import { ActionType } from 'owa-lightning-core-v2';
import { ActionTypes } from './schema/ActionTypes';
import { CAMPAIGN_RENDERED, CAMPAIGN_STARTED } from '../utils/eventNames';
import { CampaignConfig } from './schema/CampaignConfig';
import { CampaignStatus } from './schema/CampaignStatus';
import { CampaignType } from './schema/CampaignType';
import { CampaignUnit } from '../components/CampaignUnit';
import { getDataLogger } from 'telemetry';
import { onLightningPresent } from 'mini-campaign-bootstrap/lib/utils/isLightningIdPresent';

export interface CampaignRenderProps<T> {
    /* View type props to be passed when embedding campaign unit */
    campaignViewTypeProps: T;

    /* Journey function if needed */
    journey?: Journey;
}

/**
 * Class to instantiate a campaign object for referencing throughout the flow of the campaign experience.
 * This will be instantiated by developers and passed on to the CampaignUnit as props
 */
class Campaign<T> {
    private lightningId: string;
    private id: string;
    public actionTypes: ActionTypes;
    public promise: Promise<CampaignStatus>;
    public promiseResolve: (value: CampaignStatus | PromiseLike<CampaignStatus>) => void;
    private campaignType: CampaignType<T>;
    private rule: () => boolean;
    public logger: LogDataType;
    public logState: { [key: string]: boolean } = {
        CAMPAIGN_STARTED: false,
        CAMPAIGN_RENDERED: false,
        CAMPAIGN_ENDED: false,
    };

    constructor(campaignConfig: CampaignConfig) {
        this.lightningId = campaignConfig.lightningId ? campaignConfig.lightningId : null;
        this.id = campaignConfig.id;
        this.campaignType = campaignConfig.campaignType;
        this.rule = campaignConfig.rule;
        this.actionTypes = campaignConfig.actionTypes;
        this.logger = getDataLogger();
    }

    public getLightningId(): string {
        return this.lightningId;
    }

    public getId(): string {
        return this.id;
    }

    public getRule() {
        return this.rule;
    }

    public async start(): Promise<CampaignStatus> {
        /* tslint:disable:promise-must-complete */
        const self = this;
        this.promise = new Promise(resolve => (self.promiseResolve = resolve));
        /* tslint:enable:promise-must-complete */
        const ruleResult = this.rule();

        if (ruleResult) {
            let isLightningPresent = false;
            if (this.lightningId) {
                isLightningPresent = await onLightningPresent(this.lightningId);
            }
            if ((this.lightningId && isLightningPresent) || !this.lightningId) {
                if (!this.logState[CAMPAIGN_STARTED]) {
                    this.logState[CAMPAIGN_STARTED] = true;
                    this.logger?.(CAMPAIGN_STARTED, { id: this.id });
                }
                this.campaignType.start(this);
            } else {
                this.promiseResolve(null);
            }
        } else {
            this.promiseResolve(null);
        }
        return this.promise;
    }

    public end(actionType: ActionType) {
        this.promise && this.campaignType.end(this, actionType);
    }

    public render(viewProps: T): JSX.Element {
        if (!this.logState[CAMPAIGN_RENDERED]) {
            this.logState[CAMPAIGN_RENDERED] = true;
            this.logger?.(CAMPAIGN_RENDERED, { id: this.id });
        }
        return this.campaignType.render(this, viewProps);
    }

    public unit = (props: CampaignRenderProps<T>) => (
        <CampaignUnit
            journey={props.journey}
            campaign={this}
            viewProps={props.campaignViewTypeProps}
        />
    );
}

export default Campaign;
